// Beweging van de publieke site, 1-op-1 overgenomen uit de Framer-export van 27-09-2026
// (__framer__-effecten in de paginamodules en __framer__appearAnimationsContent in de HTML).
// Welke effecten aan staan: src/content/motion.ts. De markup draagt enkel data-attributen.

import Lenis from "lenis";

import { activeMotionEffects, type MotionEffect } from "../content/motion.ts";

// Meldt aan het inline script in layout.tsx dat de beweging draait (anders maakt dat na 3 s alles zichtbaar).
(window as unknown as { __wrappieMotion?: boolean }).__wrappieMotion = true;

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const $$ = <T extends Element = HTMLElement>(sel: string) => Array.from(document.querySelectorAll<T>(sel));
const on = (name: MotionEffect) => !reducedMotion && activeMotionEffects.includes(name);

let lenis: Lenis | undefined;
if (on("smoothScroll")) {
  lenis = new Lenis({ autoRaf: true, anchors: true, allowNestedScroll: true, autoToggle: true });
}

const scrollY = () => lenis?.scroll ?? window.scrollY;
const onScroll = (fn: () => void) => {
  if (lenis) lenis.on("scroll", fn);
  else window.addEventListener("scroll", fn, { passive: true });
  fn();
};

// Navigatie verbergen bij naar beneden scrollen.
if (on("navHide")) {
  const header = document.querySelector<HTMLElement>(".site-header, [data-site-header]");
  let last = scrollY();
  if (header) {
    onScroll(() => {
      const y = scrollY();
      if (Math.abs(y - last) < 4) return;
      const menuOpen = header.querySelector("[data-menu-toggle]")?.getAttribute("aria-expanded") === "true";
      header.dataset.hidden = String(!menuOpen && y > last && y > 64);
      header.dataset.scrolled = String(y > 10);
      last = y;
    });
  }
}

// Parallax zoals Framer (adjustPosition uit): verschuiving = scroll × (1 − snelheid/100).
if (on("parallax")) {
  const items = $$("[data-parallax]").map((el) => ({ el, factor: 1 - Number(el.dataset.parallax) / 100 }));
  if (items.length) {
    let frame = 0;
    onScroll(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const y = scrollY();
        for (const { el, factor } of items) el.style.setProperty("--parallax-y", `${(y * factor).toFixed(2)}px`);
      });
    });
  }
}

// Laad-animaties: starten zodra de pagina er is.
if (on("appear")) {
  requestAnimationFrame(() => $$("[data-motion='appear']").forEach((el) => (el.dataset.shown = "true")));
}

// In-beeld-effecten. "rise" speelt telkens opnieuw (Framer: transformTrigger onInView),
// "fade-up" één keer (animateOnce). Drempel 0: zodra één pixel zichtbaar is.
const inView = [on("rise") && "rise", on("fadeUp") && "fade-up"].filter(Boolean) as string[];
if (inView.length) {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        if (entry.isIntersecting) {
          el.dataset.shown = "true";
          if (el.dataset.motion === "fade-up") observer.unobserve(el);
        } else if (el.dataset.motion === "rise") {
          el.dataset.shown = "false";
        }
      }
    },
    { threshold: 0 },
  );
  for (const kind of inView) $$(`[data-motion='${kind}']`).forEach((el) => observer.observe(el));
}

// Geanimeerde iconen (design system): één keer afspelen zodra 40% zichtbaar is.
{
  const icons = $$("[data-motion='icon']");
  if (icons.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.shown = "true";
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.4 },
    );
    icons.forEach((el) => observer.observe(el));
  }
}

// WebGL-laag over de hero (Framer University "Chroma Background", stijl Liquid), enkel vanaf 1200px breed.
if (on("heroChroma") && window.matchMedia("(min-width: 1200px)").matches) {
  const layer = document.querySelector<HTMLElement>("[data-chroma]");
  if (layer) {
    layer.setAttribute("data-us-project", layer.dataset.chroma!);
    const script = document.createElement("script");
    script.src = "https://cdn.unicorn.studio/v1.2.3/unicornStudio.umd.js";
    script.onload = () => (window as unknown as { UnicornStudio?: { init: () => Promise<unknown> } }).UnicornStudio?.init();
    document.head.appendChild(script);
  }
}

export { lenis };
